import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, LogOut, ListChecks, FolderOpen, MessageSquare } from 'lucide-react';
import InstallAppButton from './InstallAppButton';
import AtriumLogo from './AtriumLogo';
import MyProfileDialog from './MyProfileDialog';
import MyTasksDialog from './MyTasksDialog';
import SavedFilesDialog from './SavedFilesDialog';
import MessagesDrawer from './MessagesDrawer';
import NotificationBell from './admin/NotificationBell';
import { signOut } from '../lib/auth';
import { isAdminLike } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_resort-map-explorer/artifacts/unjb22qg_images%20%281%29.png";

export default function Header({ settings }) {
  const logoUrl = settings?.logo_url || LOGO_URL;
  const isAuthed = typeof window !== 'undefined' && (
    !!localStorage.getItem('admin_token') || sessionStorage.getItem('atrium_guest') === '1'
  );
  const userEmail = typeof window !== 'undefined' ? localStorage.getItem('user_email') : null;
  const isGuest = typeof window !== 'undefined' && sessionStorage.getItem('atrium_guest') === '1';
  // Real signed-in users (not guests) can self-edit their profile.
  const canEditProfile = isAuthed && !isGuest;
  const [profileOpen, setProfileOpen] = useState(false);
  const [myTasksOpen, setMyTasksOpen] = useState(false);
  const [savedFilesOpen, setSavedFilesOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  // iter-92 — Fetch the signed-in user's photo + name once so the header
  // can render an avatar pill (photo on top, name underneath) instead of
  // a generic UserCircle icon. Updates whenever the profile dialog
  // closes since the user may have changed their photo or name.
  const [meSummary, setMeSummary] = useState(null);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  useEffect(() => {
    if (!adminToken || isGuest) { setMeSummary(null); return; }
    let alive = true;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        setMeSummary({ name: data?.name || '', photo_url: data?.photo_url || '', email: data?.email || '' });
      })
      .catch(() => { /* silent — header just falls back to email */ });
    return () => { alive = false; };
  }, [adminToken, isGuest, profileOpen]);

  // iter-80 — Poll unread message count every 20s while signed in.
  // Also listen for `atrium:open-messages` so other parts of the app
  // (hero CTA, dashboard tiles) can open the drawer.
  useEffect(() => {
    if (!adminToken) { setUnread(0); return undefined; }
    let alive = true;
    const refresh = () => {
      axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true })
        .then(({ data }) => { if (alive) setUnread(data?.unread || 0); })
        .catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 20000);
    const onOpen = () => setMessagesOpen(true);
    window.addEventListener('atrium:open-messages', onOpen);
    const onOpenTasks = () => setMyTasksOpen(true);
    window.addEventListener('atrium:open-my-tasks', onOpenTasks);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener('atrium:open-messages', onOpen);
      window.removeEventListener('atrium:open-my-tasks', onOpenTasks);
    };
  }, [adminToken]);
  // Refresh unread when drawer closes (it may have marked things read).
  useEffect(() => {
    if (messagesOpen || !adminToken) return;
    axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true })
      .then(({ data }) => setUnread(data?.unread || 0))
      .catch(() => {});
  }, [messagesOpen, adminToken]);
  // Admin Panel link is admin-only per the product rule. Editors and
  // engineers see the public site only; their edit-state UI is reachable
  // via the workspace tiles, not the admin panel. We read role from
  // localStorage (populated on login + during AdminPage hydration); if
  // it's missing (legacy session) we conservatively hide the link.
  const userRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const isAdmin = isAdminLike(userRole);
  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 glass"
      style={{ borderBottom: '1px solid rgba(0, 61, 165, 0.15)' }}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 h-16 flex items-center justify-between gap-2">
        {/* Logo + Admin link — min-w-0 lets this column SHRINK so the
            right-side nav (which holds Sign Out) never gets clipped on
            mobile. The TLDI / Reconstruction marks already hide below
            sm/md, so this column is naturally compact on phones. */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {isAdmin && (
            <a href="/admin/dashboard" data-testid="admin-login-link" className="flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all hover:-translate-y-0.5" style={{ backgroundColor: '#1A2E2E', color: '#F4F1EB', fontFamily: 'Manrope' }}>
              <Settings className="w-3 h-3" />
              <span className="sm:hidden">Admin</span>
              <span className="hidden sm:inline">Admin Panel</span>
            </a>
          )}
          {/* Property logo tile (Dreams Rose Hall) */}
          <img src={logoUrl} alt="Property logo" className="h-8 object-contain" />
          {/* Operator collection lockup — World of Hyatt · Inclusive Collection.
              Primary brand affiliation; placed right of the property tile. */}
          <span
            aria-hidden
            className="hidden sm:inline-block w-px h-6 mx-1.5"
            style={{ backgroundColor: 'rgba(0,61,165,0.25)' }}
          />
          <a
            href="#property-details"
            data-testid="header-hyatt-logo"
            className="hidden sm:inline-flex items-center transition-opacity hover:opacity-80"
            title="World of Hyatt · Inclusive Collection"
          >
            <img
              src="/logo-hyatt-inclusive.jpg"
              alt="World of Hyatt · Inclusive Collection"
              className="h-7 sm:h-8 object-contain"
              style={{ background: 'transparent' }}
            />
          </a>
          {/* Brand attribution: small Atrium lockup as the platform credit */}
          <span
            aria-hidden
            className="hidden sm:inline-block w-px h-5 mx-1"
            style={{ backgroundColor: 'rgba(0,61,165,0.18)' }}
          />
          <a
            href="#atrium-promo"
            data-testid="header-atrium-logo"
            className="hidden sm:inline-flex items-center transition-opacity hover:opacity-80"
            title="Powered by Atrium"
          >
            <AtriumLogo variant="inline" size={20} accent="#003DA5" text="#1A2E2E" />
          </a>
          {/* General Contractor — TLDI. Removed from the top header per
              user request (iter-94) — was overlapping with adjacent text
              on smaller laptops. Still present in the Property Details
              partner-logos strip, so visitors can still see "Operator ·
              Ownership · GC" together below the hero. */}
          <span
            aria-hidden
            className="hidden md:inline-block w-px h-5 mx-1.5"
            style={{ backgroundColor: 'rgba(0,61,165,0.18)' }}
          />
          <span
            className="hidden md:inline text-[10px] tracking-[0.15em] uppercase font-bold whitespace-nowrap"
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#7A8C8C',
            }}
          >
            Reconstruction
          </span>
        </div>

        {/* Nav links */}
        <nav className="flex items-center gap-2 sm:gap-5 flex-shrink-0">
          <InstallAppButton />
          <a
            href="#map-section"
            data-testid="nav-map"
            className="hidden sm:inline text-xs font-semibold tracking-wide transition-colors duration-200 hover:opacity-80"
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#4A5D5D',
            }}
          >
            Map
          </a>
          <a
            href="#stats-section"
            data-testid="nav-progress"
            className="hidden sm:inline text-xs font-semibold tracking-wide transition-colors duration-200 hover:opacity-80"
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#4A5D5D',
            }}
          >
            Progress
          </a>
          <a
            href="#map-section"
            data-testid="nav-explore-btn"
            onClick={(e) => {
              // Open the property-wide Project Search dialog so the
              // visitor can jump to any widget/area instantly. The
              // anchor scroll still fires as a graceful fallback if the
              // listener isn't mounted (e.g. older route).
              try { window.dispatchEvent(new CustomEvent('atrium:open-project-search')); } catch (err) { /* no-op */ }
            }}
            className="px-4 py-2 rounded-full text-xs font-bold text-white transition-all duration-200 hover:-translate-y-0.5"
            style={{
              backgroundColor: '#003DA5',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Explore
          </a>
          {canEditProfile && (
            <button
              onClick={() => setProfileOpen(true)}
              data-testid="header-edit-profile"
              title={`Edit profile${userEmail ? ' (' + userEmail + ')' : ''}`}
              className="flex flex-col items-center justify-center gap-0.5 px-1.5 transition-all hover:-translate-y-0.5"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {/* Circular avatar — photo if uploaded, initials gradient otherwise. */}
              <span
                data-testid="header-profile-avatar"
                className="block rounded-full overflow-hidden flex items-center justify-center"
                style={{
                  width: 34,
                  height: 34,
                  border: '2px solid white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                  background: meSummary?.photo_url ? '#fff' : 'linear-gradient(135deg,#1F4FBE,#003DA5)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {meSummary?.photo_url ? (
                  <img src={meSummary.photo_url} alt={meSummary?.name || userEmail || 'profile'} className="w-full h-full object-cover" />
                ) : (
                  <span aria-hidden="true">
                    {(meSummary?.name || userEmail || '?')
                      .split(/[ @]+/).filter(Boolean).slice(0, 2)
                      .map((p) => p[0]?.toUpperCase() || '').join('') || '?'}
                  </span>
                )}
              </span>
              {/* Name caption underneath. Truncates aggressively so it
                  doesn't push the rest of the nav off-screen on smaller
                  laptops. Hidden on very small viewports because the
                  avatar circle already conveys "signed in". */}
              <span
                data-testid="header-profile-name"
                className="hidden sm:block text-[9px] font-bold leading-none truncate max-w-[80px]"
                style={{ color: '#1A2E2E' }}
              >
                {(meSummary?.name || userEmail || '').split('@')[0] || 'Profile'}
              </span>
            </button>
          )}
          {isAuthed && !isGuest && (
            <button
              onClick={() => setMessagesOpen(true)}
              data-testid="header-messages"
              title="In-app messages"
              className="relative flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: '#8B5CF620',
                color: '#6D28D9',
                border: '1px solid rgba(139,92,246,0.45)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Messages</span>
              {unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: '#DC2626', color: 'white' }}
                  data-testid="header-messages-unread"
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          )}
          {isAuthed && !isGuest && (
            <button
              onClick={() => setMyTasksOpen(true)}
              data-testid="header-my-tasks"
              title="View tasks assigned to me"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: '#1F4FBE20',
                color: '#1F4FBE',
                border: '1px solid rgba(31,79,190,0.45)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <ListChecks className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">My Tasks</span>
            </button>
          )}
          {isAuthed && !isGuest && (
            <button
              onClick={() => setSavedFilesOpen(true)}
              data-testid="header-saved-files"
              title="View saved forms and snapshots"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: '#22C55E20',
                color: '#15803D',
                border: '1px solid rgba(34,197,94,0.45)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">My Files</span>
            </button>
          )}
          {isAuthed && !isGuest && adminToken && (
            <NotificationBell token={adminToken} />
          )}
          {isAuthed && (
            <button
              onClick={signOut}
              data-testid="header-signout"
              title={isGuest ? 'Exit guest mode' : `Sign out${userEmail ? ' (' + userEmail + ')' : ''}`}
              aria-label={isGuest ? 'Exit guest' : 'Sign out'}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-bold transition-all hover:-translate-y-0.5 flex-shrink-0"
              style={{
                backgroundColor: '#FFB3A620',
                color: '#FF6F59',
                border: '1px solid rgba(255,111,89,0.45)',
                fontFamily: 'Manrope, sans-serif',
              }}
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{isGuest ? 'Exit guest' : 'Sign out'}</span>
            </button>
          )}
        </nav>
      </div>
      <MyProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <MyTasksDialog open={myTasksOpen} onClose={() => setMyTasksOpen(false)} adminToken={adminToken} />
      <SavedFilesDialog open={savedFilesOpen} onClose={() => setSavedFilesOpen(false)} adminToken={adminToken} />
      <MessagesDrawer open={messagesOpen} onClose={() => setMessagesOpen(false)} />
    </header>
  );
}
